import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../../assets/icons/eye-2-icon.svg';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import {
  ApiRequestRejectedInterface,
  RequestRejectedInterface,
} from '../../../../../interface/request-rejected-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import {
  disableRequestRejectedAccountFilterApplied,
  fetchRequestRejectedAccount,
  fetchRequestRejectedAccountFilters,
  updateRequestRejectedAccountFilters,
  setSortData,
} from '../../../../../redux/slices/RequestRejectedAccountSlice';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import UTCtDateoDateConvert from '../../../../../shared-functions/UTCtDateoDateConvert';
import penaltyManagementApi from '../../../../../utils/api/penalty-management';
import ViewOverrides from '../ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request rejected component
 * @returns {any} - Request rejected component
 */
function RequestRejectedAccount() {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const {
    requestRejected,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestRejectedAccount);

  /**
   * function for closing modal
   * @param {string} message - Close message
   */
  const handleCloseModel = (message: string) => {
    if (message === 'success') {
      setIsModelOpen(false);
      setModelDetails({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsModelOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    } else {
      setIsModelOpen(false);
    }
  };

  /**
   * function for opening overlay
   * @param {any} currentData - The currentData
   */
  const handleOpenModel = (currentData: RequestRejectedInterface) => {
    const accountDetails = requestRejected.find((value) => value._id === currentData._id);
    if (accountDetails) {
      setIsModelOpen(true);
      setModelDetails({
        title: '',
        header: false,
        content: (
          <ViewOverrides
            type="Rejected"
            currentData={accountDetails}
            closeModal={handleCloseModel}
            list="account"
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {any} currentRow - The current row data
   * @returns {any} - The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestRejectedInterface) => {
    return (
      <div className="dynamic-pricing-tags">
        {currentRow.dynamicPricing.map((item) => (
          <span key={item} className="pricing-chip">
            {t(item.toLowerCase())}
          </span>
        ))}
      </div>
    );
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {any} currentRow - The current row data
   * @returns {any} - The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestRejectedInterface) => {
    return <p>{t(currentRow.ceilingType)}</p>;
  };

  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof RequestRejectedInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestRejectedInterface,
      customData: renderDynamicPricing,
      columnWidth: 200,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestRejectedInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestRejectedInterface,
      sortAllow: true,
      /**
       * maxCeilingValue custom function
       * @param {any} currentRow - The currentRow
       */ customData: (currentRow: RequestRejectedInterface) => (
        <div>{ToFixedValue(currentRow.ceilingValue)}</div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestRejectedInterface,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestRejectedInterface,
      sortAllow: true,
      /**
       * customData
       * @param {any} customData - The customData
       */
      customData: (customData: RequestRejectedInterface) => (
        <div>
          {UTCtDateoDateConvert(customData.effectiveFrom)} -{' '}
          {UTCtDateoDateConvert(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof RequestRejectedInterface,
      /**
       * Rendering action
       * @param {any} currentData - current row data
       * @returns {any} - action cell
       */
      customData: (currentData: RequestRejectedInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenModel(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Function for fetching request approved data
   * @param {any} data - Data
   */
  const fetchData = (data: ApiRequestRejectedInterface) => {
    if (!loading) {
      dispatch(fetchRequestRejectedAccount(data));
    }
  };

  /**
   * Function for sorting request approved data
   * @param {any} sortData sortData - Sort Data
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSort = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
    });
  };

  /**
   * Table pagination function
   * @param {any} value - Pagination value
   */
  const pageAndLimitChange = (value: ApiRequestRejectedInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveFrom' },
  ];
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request approved data in pdf or csv format
   * @param {any} type - export type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);

    penaltyManagementApi
      .exportAccountDataByStatus({
        payload,
        requestStatus: 'rejected',
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: RequestRejectedInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'stationNames':
                return Array.isArray(row.stationNames)
                  ? row.stationNames.join(', ')
                  : row.stationNames || t('na');

              case 'dynamicPricing':
                return row.dynamicPricing?.length
                  ? row.dynamicPricing.map((item) => t(item.toLowerCase())).join(', ')
                  : t('na');

              case 'graceInterval':
                return row.graceInterval ? `${row.graceInterval} Min` : t('na');

              case 'ceilingValue':
                return row.ceilingValue ? ToFixedValue(row.ceilingValue) : t('na');

              case 'ceilingType':
                return row.ceilingType ? t(row.ceilingType) : t('na');

              case 'effectiveFrom':
                return row.effectiveFrom && row.effectiveTo
                  ? `${UTCtDateoDateConvert(row.effectiveFrom)} - ${UTCtDateoDateConvert(row.effectiveTo)}`
                  : t('na');

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'pdf') {
            exportPDF(headers, rows, 'request-rejected');
          } else {
            exportCSV(headers, rows, 'request-rejected');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (isFilterUpdated || requestRejected.length === 0) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRequestRejectedAccountFilterApplied());
    }
  }, [filters]);

  return (
    <div>
      <ModalBox
        title={modelDetails.title}
        header={modelDetails.header}
        rightCorner={modelDetails.rightCorner}
        content={modelDetails.content}
        closeModal={() => setIsModelOpen(false)}
        open={isModelOpen}
        position="bottom-right"
      />
      <CustomTable
        version="v2"
        serialNo
        header={column}
        data={requestRejected}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        filterListLoading={filterListLoading}
        filters={filters}
        sortData={{ sortBy, sortField }}
        loading={loading}
        handleSortChange={handleSort}
        pageLimitChange={pageAndLimitChange}
        exportData={requestRejected}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        actionButton
        filtersToDispatch={updateRequestRejectedAccountFilters}
        filterListToDispatch={fetchRequestRejectedAccountFilters}
        refreshRequired
        leftTopContent={<div />}
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        height={window.innerHeight >= 700 ? 347 : 349}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default RequestRejectedAccount;
